import { apiBaseUrl } from "./config";
import type {
  Agent,
  CrmInvoiceKind,
  CrmLead,
  OrderFull,
  OrderInput,
  OrderPayment,
  PaymentKind,
  PaymentMethod,
  OrderRow,
  OrderSettingsBundle,
  OrderTotals,
  OtpResult,
  PublicOrderView,
  SignInput,
} from "./orders";
import type {
  AcademyCourse,
  AcademyEnrollmentRow,
  AcademyInstaller,
  ArchitectRow,
  Certificate,
  CertificateHistoryEntry,
  CertificateProfile,
  Day,
  Intervention,
  MaintenanceView,
  Progress,
  ProjectDetail,
  ProjectSummary,
  SessionUser,
  Specification,
  Stage,
  SurfaceDetail,
  ProjectMemberRow,
  SurfaceSummary,
  ToolkitAsset,
  UserRow,
} from "./domain";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(init.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  // ---- auth ----
  me: () => request<SessionUser>("/auth/me"),
  login: (email: string, password: string) =>
    request<SessionUser>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // ---- users (admin) ----
  users: () => request<UserRow[]>("/users"),
  createUser: (dto: Record<string, unknown>) =>
    request<UserRow>("/users", { method: "POST", body: JSON.stringify(dto) }),
  updateUser: (id: string, dto: Record<string, unknown>) =>
    request<UserRow>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(dto) }),
  userMemberships: (id: string) =>
    request<Array<{ projectId: string; access: string }>>(
      `/users/${id}/memberships`,
    ),
  resetUserPassword: (id: string, password: string) =>
    request<{ ok: boolean }>(`/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // ---- project access grants ----
  projectMembers: (projectId: string) =>
    request<ProjectMemberRow[]>(`/projects/${projectId}/members`),
  grantProject: (projectId: string, userId: string, access: string) =>
    request<{ id: string }>(`/projects/${projectId}/members`, {
      method: "POST",
      body: JSON.stringify({ userId, access }),
    }),
  revokeProjectMember: (projectId: string, userId: string) =>
    request<{ ok: boolean }>(`/projects/${projectId}/members/${userId}`, {
      method: "DELETE",
    }),

  // ---- projects ----
  projects: () => request<ProjectSummary[]>("/projects"),
  project: (id: string) => request<ProjectDetail>(`/projects/${id}`),
  createProject: (dto: Record<string, unknown>) =>
    request<{ id: string }>("/projects", {
      method: "POST",
      body: JSON.stringify(dto),
    }),

  // ---- surfaces ----
  surfaces: (projectId: string) =>
    request<SurfaceSummary[]>(`/projects/${projectId}/surfaces`),
  createSurface: (projectId: string, dto: Record<string, unknown>) =>
    request<{ id: string }>(`/projects/${projectId}/surfaces`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  surface: (id: string) => request<SurfaceDetail>(`/surfaces/${id}`),

  // ---- tracking ----
  stages: (surfaceId: string) =>
    request<{ stages: Stage[]; progress: Progress }>(
      `/surfaces/${surfaceId}/stages`,
    ),
  updateStage: (
    id: string,
    dto: { status: string; notes?: string; checklist?: Record<string, boolean> },
  ) =>
    request<Stage>(`/stages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),

  days: (projectId: string) =>
    request<{ days: Day[] }>(`/projects/${projectId}/days`),
  createDay: (projectId: string, dto: Record<string, unknown>) =>
    request<Day>(`/projects/${projectId}/days`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateDay: (id: string, dto: Record<string, unknown>) =>
    request<Day>(`/days/${id}`, { method: "PATCH", body: JSON.stringify(dto) }),

  // ---- certificate ----
  certificate: (surfaceId: string) =>
    request<{ certificate: Certificate | null; history: CertificateHistoryEntry[] }>(
      `/surfaces/${surfaceId}/certificate`,
    ),
  issueCertificate: (surfaceId: string, dto: Record<string, unknown> = {}) =>
    request<Certificate>(`/surfaces/${surfaceId}/certificate`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  reissueCertificate: (surfaceId: string, dto: Record<string, unknown> = {}) =>
    request<Certificate>(`/surfaces/${surfaceId}/certificate/reissue`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  revokeCertificate: (certificateId: string, reason: string) =>
    request<Certificate>(`/certificates/${certificateId}/revoke`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  certificatePdfUrl: (certificateId: string) =>
    `${apiBaseUrl}/certificates/${certificateId}/pdf`,

  // ---- after-sales: maintenance ----
  maintenance: (surfaceId: string) =>
    request<MaintenanceView>(`/surfaces/${surfaceId}/maintenance`),
  createMaintenance: (surfaceId: string, dto: Record<string, unknown>) =>
    request<unknown>(`/surfaces/${surfaceId}/maintenance`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateMaintenance: (id: string, dto: Record<string, unknown>) =>
    request<unknown>(`/maintenance/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),

  // ---- after-sales: interventions ----
  interventions: (filters: { status?: string; type?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString();
    return request<Intervention[]>(
      `/interventions${query ? `?${query}` : ""}`,
    );
  },
  openInterventions: () =>
    request<number>("/interventions/open-count"),
  createIntervention: (surfaceId: string, dto: Record<string, unknown>) =>
    request<Intervention>(`/surfaces/${surfaceId}/interventions`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateIntervention: (id: string, dto: Record<string, unknown>) =>
    request<Intervention>(`/interventions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),

  // ---- public lookup (home page) ----
  lookupCertificate: (code: string) =>
    request<{ token: string; matched: string }>(
      `/public/lookup?code=${encodeURIComponent(code)}`,
    ),

  // ---- settings: per-country issuing entities ----
  certificateProfiles: () =>
    request<CertificateProfile[]>("/settings/certificate-profiles"),
  createCertificateProfile: (dto: Record<string, unknown>) =>
    request<CertificateProfile>("/settings/certificate-profiles", {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateCertificateProfile: (id: string, dto: Record<string, unknown>) =>
    request<CertificateProfile>(`/settings/certificate-profiles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),
  deleteCertificateProfile: (id: string) =>
    request<{ ok: boolean }>(`/settings/certificate-profiles/${id}`, {
      method: "DELETE",
    }),

  // ---- auth: architect self-registration ----
  register: (dto: Record<string, unknown>) =>
    request<{ id: string; email: string; name: string; status: string }>(
      "/auth/register",
      { method: "POST", body: JSON.stringify(dto) },
    ),

  // ---- architect accounts (admin) ----
  architects: (status?: string) =>
    request<ArchitectRow[]>(
      `/architects${status ? `?status=${status}` : ""}`,
    ),
  pendingArchitectCount: () => request<number>("/architects/pending-count"),
  decideArchitect: (id: string, status: string) =>
    request<ArchitectRow>(`/architects/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // ---- architect toolkit: BIM/CAD assets ----
  toolkitAssets: (filters: { format?: string; finishEffect?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString();
    return request<ToolkitAsset[]>(`/toolkit/assets${query ? `?${query}` : ""}`);
  },
  uploadToolkitAsset: (file: File, meta: Record<string, string | undefined>) => {
    const form = new FormData();
    form.append("file", file);
    for (const [k, v] of Object.entries(meta)) {
      if (v !== undefined && v !== "") form.append(k, String(v));
    }
    return request<ToolkitAsset>("/toolkit/assets", {
      method: "POST",
      body: form,
    });
  },
  updateToolkitAsset: (id: string, dto: Record<string, unknown>) =>
    request<ToolkitAsset>(`/toolkit/assets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),
  deleteToolkitAsset: (id: string) =>
    request<{ ok: boolean }>(`/toolkit/assets/${id}`, { method: "DELETE" }),
  toolkitAssetUrl: (id: string) => `${apiBaseUrl}/toolkit/assets/${id}/download`,

  // ---- architect toolkit: capitolato ----
  specifications: (locale?: string) =>
    request<Specification[]>(
      `/toolkit/specifications${locale ? `?locale=${locale}` : ""}`,
    ),
  createSpecification: (dto: Record<string, unknown>) =>
    request<Specification>("/toolkit/specifications", {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateSpecification: (id: string, dto: Record<string, unknown>) =>
    request<Specification>(`/toolkit/specifications/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),
  deleteSpecification: (id: string) =>
    request<{ ok: boolean }>(`/toolkit/specifications/${id}`, {
      method: "DELETE",
    }),

  // ---- architect toolkit: samples ----
  requestSamples: (dto: Record<string, unknown>) =>
    request<Intervention>("/toolkit/sample-requests", {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  sampleRequests: () => request<Intervention[]>("/toolkit/sample-requests"),

  // ---- academy: training & credentials ----
  academyCourses: () => request<AcademyCourse[]>("/academy/courses"),
  createAcademyCourse: (dto: Record<string, unknown>) =>
    request<AcademyCourse>("/academy/courses", {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  updateAcademyCourse: (id: string, dto: Record<string, unknown>) =>
    request<AcademyCourse>(`/academy/courses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dto),
    }),
  academyInstallers: () => request<AcademyInstaller[]>("/academy/installers"),
  academyInstaller: (id: string) =>
    request<AcademyInstaller>(`/academy/installers/${id}`),
  academyMe: () => request<AcademyInstaller>("/academy/me"),
  recordEnrollment: (installerId: string, dto: Record<string, unknown>) =>
    request<{ enrollment: AcademyEnrollmentRow }>(
      `/academy/installers/${installerId}/enrollments`,
      { method: "POST", body: JSON.stringify(dto) },
    ),
  deleteEnrollment: (id: string) =>
    request<{ ok: boolean }>(`/academy/enrollments/${id}`, { method: "DELETE" }),

  // ---- media ----
  /**
   * Photos and videos. XHR instead of fetch so the caller can show upload
   * progress (a phone video is tens of MB on a site connection). Metadata
   * fields go before the file: the API checks access before it streams.
   */
  upload: (
    file: File | Blob,
    meta: {
      kind: string;
      surfaceId?: string;
      projectId?: string;
      stageId?: string;
      projectDayId?: string;
      orderId?: string;
      orderRoomId?: string;
      caption?: string;
    },
    opts: { filename?: string; onProgress?: (fraction: number) => void } = {},
  ) =>
    new Promise<{ id: string; url: string; mimeType?: string | null }>((resolve, reject) => {
      const form = new FormData();
      for (const [k, v] of Object.entries(meta)) {
        if (v) form.append(k, String(v));
      }
      form.append("file", file, opts.filename ?? (file instanceof File ? file.name : "photo.jpg"));
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${apiBaseUrl}/media`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("accept", "application/json");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && opts.onProgress) opts.onProgress(e.loaded / e.total);
      };
      xhr.onerror = () => reject(new ApiError(0, "network error"));
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new ApiError(xhr.status, "bad response"));
          }
        } else reject(new ApiError(xhr.status, xhr.responseText || xhr.statusText));
      };
      xhr.send(form);
    }),

  // ---- orders (admin + sales) ----
  resolveMapsLink: (url: string) =>
    request<{ lat: number; lng: number }>(`/orders/geo/resolve?url=${encodeURIComponent(url)}`),
  orders: (filters: { status?: string; q?: string; assignee?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString();
    return request<OrderRow[]>(`/orders${query ? `?${query}` : ""}`);
  },
  order: (id: string) => request<OrderFull>(`/orders/${id}`),
  orderAgents: () => request<Agent[]>("/orders/agents"),
  assignOrder: (id: string, assigneeId: string) =>
    request<OrderFull>(`/orders/${id}/assign`, { method: "POST", body: JSON.stringify({ assigneeId }) }),
  createOrder: (dto: OrderInput) =>
    request<OrderFull>("/orders", { method: "POST", body: JSON.stringify(dto) }),
  updateOrder: (id: string, dto: OrderInput) =>
    request<OrderFull>(`/orders/${id}`, { method: "PATCH", body: JSON.stringify(dto) }),
  quoteOrder: (dto: Partial<OrderInput>) =>
    request<OrderTotals>("/orders/quote", { method: "POST", body: JSON.stringify(dto) }),
  renderOrder: (id: string) =>
    request<{ key: string; sha256: string }>(`/orders/${id}/render`, { method: "POST" }),
  orderPdfUrl: (id: string) => `${apiBaseUrl}/orders/${id}/pdf`,
  sendOrder: (id: string, dto: { via: "whatsapp" | "email"; to?: string }) =>
    request<OrderFull>(`/orders/${id}/send`, { method: "POST", body: JSON.stringify(dto) }),
  orderOtp: (id: string, channel?: "whatsapp" | "email") =>
    request<OtpResult>(`/orders/${id}/otp`, { method: "POST", body: JSON.stringify({ channel }) }),
  signOrder: (id: string, dto: SignInput) =>
    request<{ ok: true; signedPdfUrl: string }>(`/orders/${id}/sign`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
  approveOrder: (id: string) => request<OrderFull>(`/orders/${id}/approve`, { method: "POST" }),
  cancelOrder: (id: string, reason: string) =>
    request<OrderFull>(`/orders/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  reviseOrder: (id: string) => request<OrderFull>(`/orders/${id}/revise`, { method: "POST" }),
  duplicateOrder: (id: string) => request<OrderFull>(`/orders/${id}/duplicate`, { method: "POST" }),
  deleteMedia: (id: string) => request<{ ok: boolean }>(`/media/${id}`, { method: "DELETE" }),
  crmInvoice: (id: string, kind: CrmInvoiceKind) =>
    request<OrderFull>(`/orders/${id}/crm-invoice`, { method: "POST", body: JSON.stringify({ kind }) }),
  requestPayment: (id: string, body: { kind: PaymentKind; amount?: number; email?: boolean }) =>
    request<OrderPayment>(`/orders/${id}/payments/request`, { method: "POST", body: JSON.stringify(body) }),
  recordPayment: (id: string, body: { kind: PaymentKind; method: PaymentMethod; amount: number; note?: string; paidAt?: string }) =>
    request<OrderPayment>(`/orders/${id}/payments/manual`, { method: "POST", body: JSON.stringify(body) }),
  whatsappPaymentLink: (id: string, paymentId: string) =>
    request<{ result: string }>(`/orders/${id}/payments/${paymentId}/whatsapp`, { method: "POST" }),
  cancelPaymentLink: (id: string, paymentId: string) =>
    request<{ ok: true }>(`/orders/${id}/payments/${paymentId}/cancel`, { method: "POST" }),
  stripeTest: () => request<{ ok: true; mode: string | null; livemode: boolean; currencies: string[] }>("/payments/stripe/test"),
  deleteOrder: (id: string) => request<{ ok: true }>(`/orders/${id}`, { method: "DELETE" }),
  convertOrder: (id: string) =>
    request<{ projectId: string }>(`/orders/${id}/convert`, { method: "POST" }),
  orderSettings: () => request<OrderSettingsBundle>("/orders/settings"),
  updateOrderSettings: (dto: Record<string, unknown>) =>
    request<OrderSettingsBundle>("/orders/settings", { method: "PATCH", body: JSON.stringify(dto) }),
  crmLeads: (filters: { phone?: string; q?: string }) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString();
    return request<CrmLead[]>(`/orders/leads?${query}`);
  },
  updateCrmLead: (leadId: number, dto: Record<string, string | undefined>) =>
    request<{ ok: boolean }>(`/orders/leads/${leadId}`, { method: "PATCH", body: JSON.stringify(dto) }),
  uploadIdDocument: (id: string, side: "front" | "back", file: Blob) => {
    const form = new FormData();
    form.append("file", file, `eid-${side}.jpg`);
    return request<OrderFull>(`/orders/${id}/id-document?side=${side}`, { method: "POST", body: form });
  },
  idDocumentUrl: (id: string, side: "front" | "back") => `${apiBaseUrl}/orders/${id}/id-document/${side}`,
  deleteIdDocument: (id: string, side: "front" | "back") =>
    request<OrderFull>(`/orders/${id}/id-document/${side}`, { method: "DELETE" }),

  // ---- orders: customer side (no login) ----
  publicOrder: (token: string) => request<PublicOrderView>(`/public/orders/${token}`),
  publicOrderPdfUrl: (token: string) => `${apiBaseUrl}/public/orders/${token}/pdf`,
  publicOrderOtp: (token: string, channel?: "whatsapp" | "email") =>
    request<OtpResult>(`/public/orders/${token}/otp`, { method: "POST", body: JSON.stringify({ channel }) }),
  publicOrderSign: (token: string, dto: SignInput) =>
    request<{ ok: true; signedPdfUrl: string }>(`/public/orders/${token}/sign`, {
      method: "POST",
      body: JSON.stringify(dto),
    }),
};
